import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Flex, Result, Spin } from 'antd';

import { Dispatch, iRootState } from '../../../../../store';
import { useAsync } from '../../../../../hooks';
import { isSecurableTypesPayload, LoadedSecurableTypes } from '../../../../../utils/parseSecurableTypes';

import useSecTreeItem from '../../useSecTreeItem';
import { resetStoredExpanded } from '../../treeExpandedStore';
import FormTree from '../FromTree';
import Form, { LoadedPermissions } from './Form';

import '../PermissionsTable.css';

type PermissionsRequest = {
  treeItem: string;
  status: 'loading' | 'ready' | 'error';
};

const mapState = (state: iRootState) => ({
  data: state.settingSecTree.data,
  secTreeHierarchy: state.settingSecTree.secTreeHierarchy
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getHierarchy: d.settingSecTree.getHierarchy,
    getData: d.settingSecTree.getData,
    getSecurableTypes: d.settingSecTree.getSecurableTypes,
    getNodeACL: d.settingSecTree.getNodeACL,
    updateOrCreateACL: d.settingSecTree.updateOrCreateACL,
    reset: d.settingSecTree.reset
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

const Index: FC<IProps> = ({
  data,
  getData,
  updateOrCreateACL,
  getHierarchy,
  secTreeHierarchy,
  getSecurableTypes,
  getNodeACL,
  reset
}) => {
  const [runGetData, isLoadingGetData, isGetDataLoadingError] = useAsync(getData, true);
  const [runGetHierarchy, isLoadingGetHierarchy, isGetHierarchyError] = useAsync(getHierarchy);
  const [runGetNodeACL, , isGetHNodeACLError] = useAsync(getNodeACL);
  const { treeItem, isTreeItemReady } = useSecTreeItem(secTreeHierarchy);
  const [loadedPermissions, setLoadedPermissions] = useState<LoadedSecurableTypes | null>(null);
  const [permissionsRequest, setPermissionsRequest] = useState<PermissionsRequest | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);

  const [runUpdateOrCreate] = useAsync(updateOrCreateACL);

  useEffect(() => {
    runGetData();
    runGetHierarchy();
    runGetNodeACL({ root: '', roleId: '' });
    return () => {
      resetStoredExpanded();
      reset();
    };
  }, []);

  // Local state keyed by treeItem: model fetch returns data but must not rely on shared Redux
  // (concurrent requests would overwrite each other during fast tree navigation).
  useEffect(() => {
    if (!isTreeItemReady || !treeItem) {
      return;
    }

    let cancelled = false;
    const requestTreeItem = treeItem;

    setPermissionsRequest({ treeItem: requestTreeItem, status: 'loading' });

    const loadPermissions = async () => {
      try {
        const result = await getSecurableTypes(requestTreeItem);

        if (cancelled) {
          return;
        }

        if (isSecurableTypesPayload(result)) {
          setLoadedPermissions({
            treeItem: requestTreeItem,
            securableTypes: result.securableTypes
          });
          setPermissionsRequest({ treeItem: requestTreeItem, status: 'ready' });
          return;
        }

        // tslint:disable-next-line:no-console
        console.error('[SecTree/Rights] Failed to load securable types', {
          treeItem: requestTreeItem,
          error: result
        });
        setPermissionsRequest({ treeItem: requestTreeItem, status: 'error' });
      } catch (error) {
        if (cancelled) {
          return;
        }

        // tslint:disable-next-line:no-console
        console.error('[SecTree/Rights] Failed to load securable types', {
          treeItem: requestTreeItem,
          error
        });
        setPermissionsRequest({ treeItem: requestTreeItem, status: 'error' });
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [getSecurableTypes, isTreeItemReady, treeItem]);

  const isPermissionsLoading =
    permissionsRequest?.treeItem === treeItem && permissionsRequest.status === 'loading';
  const isPermissionsError =
    permissionsRequest?.treeItem === treeItem && permissionsRequest.status === 'error';

  const handleCancel = async () => {
    await getNodeACL({ root: '', roleId: '' });
    setFormResetKey(key => key + 1);
  };

  const isPageLoading = isLoadingGetData || isLoadingGetHierarchy;
  const isPageLoadingError =
    isGetDataLoadingError || isGetHierarchyError || isGetHNodeACLError;

  return (
    <>
      {!isPageLoading && !isPageLoadingError && (
        <div className="sec-tree-rights-page">
          <div className="sec-tree-layout">
            <div className="sec-tree-layout__tree">
              <FormTree defaultSelected={treeItem} secTreeHierarchy={secTreeHierarchy} />
            </div>
            <div className="sec-tree-layout__content">
              <Form
                data={data}
                secTreeHierarchy={secTreeHierarchy}
                treeItem={treeItem}
                loadedPermissions={loadedPermissions}
                isPermissionsLoading={isPermissionsLoading}
                isPermissionsError={isPermissionsError}
                formResetKey={formResetKey}
                onSubmitAction={runUpdateOrCreate}
                onCancel={handleCancel}
              />
            </div>
          </div>
        </div>
      )}
      {isPageLoading && (
        <Flex justify="center" align="center" style={{ minHeight: 240 }}>
          <Spin size="large" />
        </Flex>
      )}
      {isPageLoadingError && <Result status="500" title="Ошибка сервера" />}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Index);
